import React from 'react';
import { Typography } from 'components/atoms';
import ReactStars from 'react-rating-stars-component';
import './index.scss';

interface Review {
    id: string;
    comment: string;
    rating: number;
    reviewedAt: string;
    reviewedBy: string;
    goalId?: string;
}

interface SubTask {
    id: string;
    title: string;
    description: string;
    deadline: string;
    status: 'Completed' | 'Pending' | 'In Progress';
    priority: 'High' | 'Medium' | 'Low';
}

interface Goal {
    id: string;
    title: string;
    description: string;
    priority: 'High' | 'Medium' | 'Low';
    status: 'Completed' | 'Pending' | 'In Progress';
    reviews: Review[];
    assignedTo: string;
    quarter: string;
    subtasks: SubTask[];
    isParentTask?: boolean;
    combinedGoalId?: string;
    acceptanceStatus?: string | null;
}

interface TaskCardProps {
    task: Goal;
    onCardClick: () => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, onCardClick }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed':
                return '#52c41a';
            case 'In Progress':
                return '#faad14';
            case 'Pending':
                return '#ff4d4f';
            default:
                return '#8c8c8c';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'High':
                return '#ff4d4f';
            case 'Medium':
                return '#faad14';
            case 'Low':
                return '#52c41a';
            default:
                return '#8c8c8c';
        }
    };

    const getAcceptanceStatusColor = (status: string | null | undefined) => {
        switch (status) {
            case 'Accepted':
                return '#52c41a';
            case 'Declined':
                return '#ff4d4f';
            case 'Pending':
            default:
                return '#faad14';
        }
    };

    const getAcceptanceStatusText = (status: string | null | undefined) => {
        if (!status || status === 'Pending') return 'Pending Acceptance';
        return status;
    };

    const getSubtasksSummary = () => {
        const total = task.subtasks.length;
        const completed = task.subtasks.filter(st => st.status === 'Completed').length;
        const inProgress = task.subtasks.filter(st => st.status === 'In Progress').length;
        const pending = task.subtasks.filter(st => st.status === 'Pending').length;
        const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { total, completed, inProgress, pending, completionPercentage };
    };

    const subtasksSummary = getSubtasksSummary();
    const averageRating = task.reviews.length > 0
        ? task.reviews.reduce((sum, review) => sum + review.rating, 0) / task.reviews.length
        : 0;

    return (
        <div 
            className={`task-card status-${task.status.toLowerCase()}`}
            onClick={onCardClick}
        >
            <div className="task-card__header">
                <div className="task-card__title">
                    <Typography type="heading5" text={task.title} />
                    <span
                        className="priority-indicator"
                        style={{ backgroundColor: getPriorityColor(task.priority) }}
                    />
                </div>
            </div>

            <div className="task-card__metrics">
                <div className="completion-bar">
                    <div 
                        className="completion-progress" 
                        style={{ width: `${subtasksSummary.completionPercentage}%` }}
                    />
                    <Typography 
                        type="small_medium" 
                        text={`${subtasksSummary.completionPercentage}%`}
                        className="completion-text"
                    />
                </div>

                <div className="metrics-row">
                    <div className="metric-item">
                        <img src="/icons/list-check.svg" alt="Total tasks" className="metric-icon" />
                        <Typography type="small_medium" text={subtasksSummary.total.toString()} />
                    </div>
                    {subtasksSummary.completed > 0 && (
                        <div className="metric-item completed">
                            <img src="/icons/list-check.svg" alt="Completed tasks" className="metric-icon" />
                            <Typography type="small_medium" text={subtasksSummary.completed.toString()} />
                        </div>
                    )}
                    {subtasksSummary.inProgress > 0 && (
                        <div className="metric-item in-progress">
                            <img src="/icons/clock.svg" alt="In progress tasks" className="metric-icon" />
                            <Typography type="small_medium" text={subtasksSummary.inProgress.toString()} />
                        </div>
                    )}
                </div>
            </div>

            <div className="task-card__footer">
                <div className="status-badges">
                    <div className="badge-container">
                        <Typography type="small_medium" text="Status:" className="badge-label" />
                        <div className="status-badge" style={{ backgroundColor: getStatusColor(task.status) }}>
                            {task.status}
                        </div>
                    </div>
                    <div className="badge-container">
                        <Typography type="small_medium" text="Acceptance:" className="badge-label" />
                        <div className="acceptance-badge" style={{ backgroundColor: getAcceptanceStatusColor(task.acceptanceStatus) }}>
                            {getAcceptanceStatusText(task.acceptanceStatus)}
                        </div>
                    </div>
                </div>
                {averageRating > 0 && (
                    <div className="rating-display">
                        <ReactStars
                            count={5}
                            value={averageRating}
                            edit={false}
                            size={16}
                            activeColor="#ffd700"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}; 